import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Lunel' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Lunel',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Lunel' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Lunel. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous à Lunel le week-end ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, notre service est disponible 7 jours sur 7, week-ends inclus. Besoin d\'une réparation de volet roulant un samedi ou d\'un vitrier en urgence un dimanche à Lunel ? Nous nous déplaçons sous 24 à 48h.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quels types de menuiseries réparez-vous à Lunel ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Tous types : baies coulissantes aluminium, volets roulants électriques et manuels, fenêtres PVC, alu et bois, stores et BSO. Nous intervenons aussi en vitrerie : remplacement de vitre cassée, double vitrage, vitrage embué.',
      },
    },
  ],
};

export default function LunelPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Lunel | DFPRO"
        description="Baie coulissante, volet roulant ou vitrage en panne à Lunel ? DFPRO intervient 24-48h, 7j/7 dans l'est Hérault. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-lunel/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Lunel"
        subtitle="Entre Montpellier et Nîmes, nos techniciens réparent baies coulissantes, volets roulants et vitrages à Lunel. Devis gratuit, intervention 24-48h, 7j/7."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Baie ou volet en panne à Lunel ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent à Lunel et dans tout l'est héraultais, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          À Lunel, nous intervenons sur tous les types de pannes : baie coulissante qui coince dans un pavillon récent, volet roulant bloqué dans le centre ancien, fenêtre dont la crémone est grippée, vitrage à remplacer. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a> pour un diagnostic gratuit.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/lunel-baie-01.webp"
              alt="Réparation de baie vitrée coulissante à Lunel"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante : roulettes usées, rail encrassé</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Une baie qui <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince ou déraille</Link> signale un problème de roulettes ou de rail. Dans les pavillons de Lunel, l'usure est souvent liée à un manque de lubrification. Ne forcez surtout pas : le vantail pourrait sortir du rail.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous proposons le <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">changement de roulettes</Link>, la <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparation de rail</Link> et le <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link> précis des vantaux. Toutes marques supportées.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant : moteur, sangle, blocage</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants</Link> sont la panne numéro un à Lunel. <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">Volet bloqué</Link>, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur à changer</Link>, <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">sangle</Link> usée : nous diagnostiquons et réparons sur place.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous réalisons la <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> des volets manuels et intervenons sur tous types : électriques filaires, radio, solaires.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/lunel-volet-02.jpg"
              alt="Réparation de volet roulant à Lunel"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/lunel-vitrage-03.avif"
              alt="Remplacement de vitrage à Lunel"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier et réparation fenêtre à Lunel</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link>, <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link>, <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">urgence vitrier</Link> : notre équipe intervient sur tous les fronts. <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">Vitrage embué</Link> ? Nous remplaçons le vitrage sans changer la fenêtre.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Pour les <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">fenêtres</Link> : <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link>, <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battants</Link>, <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémones</Link>. Tous matériaux.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Lunel sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Lunel : centre-ville, quartiers résidentiels, zones d'activités. Communes voisines : Marsillargues, Saint-Just, Lansargues, Valergues, <Link to="/depannage-menuiserie-mauguio/" className="text-accent-500 font-semibold hover:underline">Mauguio</Link>. Retrouvez-nous sur la <Link to="/" className="text-accent-500 font-semibold hover:underline">page d'accueil DFPRO Hérault</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}